import React, { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger, prefersReducedMotion } from '../../utils/gsap';

/**
 * DreamChapter — The USA Dream & Collapse (300vh pinned timeline scene)
 *
 * A horizontal timeline progresses as the visitor scrolls.
 * Milestones: Scholarship → A-Levels → USA → Visa → Collapse → FAST
 *
 * The timeline fractures at the collapse moment.
 * A major moment of stillness: "THE PLAN DIDN'T WORK."
 * Then: "SO I FOUND ANOTHER WAY FORWARD."
 * FAST emerges — the timeline resets and draws fresh.
 */
const DreamChapter = () => {
  const wrapperRef = useRef(null);
  const sceneRef = useRef(null);
  const annotationRef = useRef(null);
  const timelineTrackRef = useRef(null);
  const milestonesRef = useRef([]);
  const collapseRef = useRef(null);
  const stillnessRef = useRef(null);
  const pivotRef = useRef(null);
  const fastRef = useRef(null);
  const fastLineRef = useRef(null);

  const milestones = [
    { year: '2021', label: 'BEACONHOUSE\nSCHOLARSHIP', color: 'rgba(212,163,89,0.9)' },
    { year: '2022', label: 'A-LEVELS\nA-STARS', color: 'rgba(212,163,89,0.9)' },
    { year: '2022', label: 'PACE UNIV.\nUSA ACCEPTANCE', color: 'rgba(56,189,248,0.9)' },
    { year: '2023', label: 'VISA\nAPPLICATION', color: 'rgba(56,189,248,0.7)' },
    { year: '2023', label: 'HIDDEN\nCOSTS', color: 'rgba(244,63,94,0.8)' },
  ];

  useEffect(() => {
    if (prefersReducedMotion()) return;

    let ctx;
    const setup = () => {
      ctx = gsap.context(() => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: wrapperRef.current,
            start: 'top top',
            end: '+=340%',
            pin: sceneRef.current,
            scrub: 1.0,
            anticipatePin: 1,
          }
        });

        // 0–10%: annotation appears
        tl.fromTo(annotationRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.3 },
          0
        )

        // 10–30%: timeline track draws, milestones pop in
        .fromTo(timelineTrackRef.current,
          { scaleX: 0, transformOrigin: 'left center' },
          { scaleX: 1, ease: 'power2.inOut', duration: 1.2 },
          0.3
        );

        // Stagger milestones
        milestonesRef.current.forEach((el, i) => {
          if (!el) return;
          tl.fromTo(el,
            { opacity: 0, y: i % 2 === 0 ? -25 : 25, scale: 0.8 },
            { opacity: 1, y: 0, scale: 1, ease: 'back.out(1.5)', duration: 0.35 },
            0.5 + i * 0.25
          );
        });

        // === PHASE 2: COLLAPSE MOMENT ===
        // Timeline distorts and clears
        tl.to(timelineTrackRef.current, {
          scaleY: 3,
          skewX: 5,
          opacity: 0,
          ease: 'power3.in',
          duration: 0.4
        }, 2.2)
        .to(milestonesRef.current.filter(Boolean), {
          opacity: 0,
          y: (i) => (i % 2 === 0 ? -20 : 20),
          duration: 0.4
        }, 2.2)

        // COLLAPSE text ("THE PLAN DIDN'T WORK.") — held for significant scroll time
        .fromTo(collapseRef.current,
          { opacity: 0, scale: 1.08 },
          { opacity: 1, scale: 1, ease: 'power4.out', duration: 0.5 },
          2.5
        )
        // Silence hold
        .to({}, { duration: 1.0 }, 2.9)

        // Fade out collapse text
        .to(collapseRef.current, {
          opacity: 0,
          scale: 0.92,
          duration: 0.4,
          ease: 'power2.in'
        }, 3.9)

        // === PHASE 3: PIVOT ("SO I FOUND ANOTHER WAY FORWARD.") ===
        .fromTo(pivotRef.current,
          { opacity: 0, scale: 0.95, y: 25 },
          { opacity: 1, scale: 1, y: 0, ease: 'power3.out', duration: 0.6 },
          4.3
        )
        // Dedicated reading hold for the pivot statement
        .to({}, { duration: 1.2 }, 4.9)
        .to(pivotRef.current, {
          opacity: 0,
          y: -20,
          duration: 0.4,
          ease: 'power2.in'
        }, 6.1)

        // === PHASE 4: FAST-NUCES EMERGES WITH A CLEAR HOLD ===
        .fromTo(fastRef.current,
          { opacity: 0, scale: 0.85, y: 20 },
          { opacity: 1, scale: 1, y: 0, ease: 'back.out(1.4)', duration: 0.7 },
          6.4
        )
        .fromTo(fastLineRef.current,
          { scaleX: 0, transformOrigin: 'center center' },
          { scaleX: 1, ease: 'power2.inOut', duration: 0.6 },
          6.6
        )
        // Dedicated hold so FAST-NUCES is fully seen and appreciated
        .to({}, { duration: 1.2 }, 7.1)
        // Seamless exit into Chapter 6
        .to(fastRef.current, {
          opacity: 0,
          y: -25,
          duration: 0.5,
          ease: 'power2.in'
        }, 8.3);
      });
    };

    ScrollTrigger.refresh();
    setup();
    return () => { if (ctx) ctx.revert(); };
  }, []);

  return (
    <section ref={wrapperRef} className="dream-scroll-wrapper">
      <div ref={sceneRef} className="dream-scene">

        <div className="dream-bg" aria-hidden="true" />

        <div ref={annotationRef} className="dream-annotation font-mono">
          CH. 04–05 &nbsp;// THE DREAM &amp; ITS COLLAPSE
        </div>

        {/* Horizontal timeline track */}
        <div className="dream-timeline-container">
          <div ref={timelineTrackRef} className="dream-track" />

          {milestones.map((m, i) => (
            <div
              key={i}
              ref={(el) => (milestonesRef.current[i] = el)}
              className="dream-milestone"
              style={{ left: `${10 + i * 20}%`, top: i % 2 === 0 ? '30%' : '56%' }}
            >
              <div className="dream-node" style={{ background: m.color }} />
              <div
                className="dream-connector"
                style={{ top: i % 2 === 0 ? '100%' : undefined, bottom: i % 2 !== 0 ? '100%' : undefined }}
              />
              <div className="dream-milestone-label font-mono">
                <span className="dream-year">{m.year}</span>
                <span className="dream-ms-text" style={{ color: m.color }}>{m.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* COLLAPSE phrase */}
        <div ref={collapseRef} className="dream-collapse">
          <div className="dream-collapse-text font-display">
            THE PLAN<br />DIDN'T WORK.
          </div>
        </div>

        {/* STILLNESS */}
        <div ref={stillnessRef} className="dream-stillness font-mono">
          &nbsp;
        </div>

        {/* PIVOT */}
        <div ref={pivotRef} className="dream-pivot">
          <div className="dream-pivot-text font-display">
            SO I FOUND<br />
            <span className="dream-pivot-accent">ANOTHER WAY FORWARD.</span>
          </div>
        </div>

        {/* FAST emergence */}
        <div ref={fastRef} className="dream-fast">
          <div ref={fastLineRef} className="dream-fast-line" />
          <div className="dream-fast-label font-mono">A NEW PATH</div>
          <div className="dream-fast-name font-display">FAST-NUCES</div>
          <div className="dream-fast-sub font-mono">LAHORE &nbsp;·&nbsp; COMPUTER SCIENCE</div>
        </div>
      </div>

      <style>{`
        .dream-scroll-wrapper {
          position: relative;
          height: 380vh;
          background: #080a0f;
        }

        .dream-scene {
          position: relative;
          width: 100%;
          height: 100vh;
          overflow: hidden;
          background: #080a0f;
          display: flex;
          align-items: center;
          justify-content: center;
          will-change: transform;
        }

        .dream-bg {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at 50% 60%, rgba(56,189,248,0.02) 0%, transparent 70%);
          pointer-events: none;
        }

        .dream-annotation {
          position: absolute;
          top: 8vh;
          left: 5vw;
          font-size: 0.68rem;
          letter-spacing: 0.2em;
          color: rgba(212, 163, 89, 0.5);
          will-change: opacity;
        }

        /* Timeline */
        .dream-timeline-container {
          position: absolute;
          width: 90%;
          left: 5%;
          top: 0;
          height: 100%;
        }

        .dream-track {
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(to right, transparent, rgba(212,163,89,0.4) 10%, rgba(244,63,94,0.4) 85%, transparent);
          will-change: transform, opacity;
        }

        .dream-milestone {
          position: absolute;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          will-change: opacity, transform;
        }

        .dream-node {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          box-shadow: 0 0 12px currentColor;
        }

        .dream-connector {
          position: absolute;
          width: 1px;
          height: 40px;
          background: rgba(255,255,255,0.1);
          left: 50%;
          transform: translateX(-50%);
        }

        .dream-milestone-label {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 0.15rem;
        }

        .dream-year {
          font-size: 0.58rem;
          letter-spacing: 0.12em;
          color: rgba(107,114,128,0.6);
        }

        .dream-ms-text {
          font-size: 0.6rem;
          letter-spacing: 0.08em;
          white-space: pre;
          line-height: 1.4;
          text-align: center;
        }

        /* Collapse */
        .dream-collapse {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          will-change: opacity, transform;
        }

        .dream-collapse-text {
          font-size: clamp(3rem, 8vw, 7rem);
          font-weight: 800;
          line-height: 1.0;
          letter-spacing: -0.03em;
          color: rgba(244, 63, 94, 0.85);
          text-align: center;
        }

        /* Stillness — empty space */
        .dream-stillness {
          position: absolute;
          inset: 0;
          will-change: opacity;
        }

        /* Pivot */
        .dream-pivot {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          will-change: opacity, transform;
        }

        .dream-pivot-text {
          font-size: clamp(2rem, 5vw, 4.5rem);
          font-weight: 700;
          line-height: 1.15;
          text-align: center;
          color: rgba(243,244,246,0.85);
        }

        .dream-pivot-accent {
          color: var(--accent-gold);
        }

        /* FAST */
        .dream-fast {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          will-change: opacity, transform;
        }

        .dream-fast-line {
          width: 80px;
          height: 1px;
          background: rgba(212,163,89,0.5);
          margin-bottom: 1rem;
          will-change: transform;
        }

        .dream-fast-label {
          font-size: 0.65rem;
          letter-spacing: 0.22em;
          color: rgba(107,114,128,0.7);
        }

        .dream-fast-name {
          font-size: clamp(2.8rem, 6.8vw, 5.8rem);
          font-weight: 800;
          letter-spacing: -0.03em;
          color: #f3f4f6;
          white-space: nowrap;
        }

        .dream-fast-sub {
          font-size: 0.65rem;
          letter-spacing: 0.18em;
          color: rgba(212,163,89,0.6);
          margin-top: 0.25rem;
        }

        @media (max-width: 768px) {
          .dream-scroll-wrapper {
            height: 360vh;
          }

          /* Annotation text safely below mobile topbar */
          .dream-annotation {
            top: calc(56px + 12px);
            left: 5vw;
            font-size: 0.62rem;
            letter-spacing: 0.16em;
          }

          .dream-timeline-container {
            width: 96%;
            left: 2%;
          }

          .dream-node {
            width: 8px;
            height: 8px;
          }

          .dream-connector {
            height: 24px;
          }

          .dream-year {
            font-size: 0.5rem;
          }

          .dream-ms-text {
            font-size: 0.44rem;
            letter-spacing: 0.05em;
            line-height: 1.2;
          }

          /* Big cinematic collapse text */
          .dream-collapse-text {
            font-size: clamp(1.8rem, 8vw, 3.2rem);
            white-space: normal;
            text-align: center;
            padding: 0 1.25rem;
            line-height: 1.15;
          }

          /* Pivot text */
          .dream-pivot-text {
            font-size: clamp(1.4rem, 6.5vw, 2.4rem);
            white-space: normal;
            text-align: center;
            padding: 0 1rem;
            line-height: 1.25;
          }

          /* FAST section — stacked centered, prevents text clipping */
          .dream-fast-name {
            font-size: clamp(2rem, 9vw, 3.4rem);
            white-space: normal;
            word-break: break-word;
            text-align: center;
            max-width: 90vw;
          }

          .dream-fast-sub {
            font-size: 0.58rem;
            letter-spacing: 0.14em;
          }
        }
      `}</style>
    </section>
  );
};

export default DreamChapter;
